import { useState } from "react";
import { Check, Copy, Link2, Loader2, Lock, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { useDocEditLink } from "@/hooks/documents";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface ShareEditLinkPanelProps {
  document: DocumentWithMetadata;
  isOpen: boolean;
}

/**
 * Inline owner control for a document's public "magic edit link":
 * generate / copy / PIN-protect / rotate / revoke. Rendered directly inside the
 * document drawer so the link can be managed in place — no separate sheet.
 */
export const ShareEditLinkPanel = ({ document, isOpen }: ShareEditLinkPanelProps) => {
  const { link, isLoading, generate, setPin, revoke } = useDocEditLink(document.id, isOpen);

  const [copied, setCopied] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [showPinForm, setShowPinForm] = useState(false);

  const fullUrl = link ? `${window.location.origin}${link.path}` : "";

  const handleCopy = async () => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked — the field is selectable as a fallback */
    }
  };

  const handleSavePin = async () => {
    if (pinValue.trim().length < 4) return;
    await setPin.mutateAsync(pinValue.trim());
    setPinValue("");
    setShowPinForm(false);
  };

  const busy = generate.isPending || setPin.isPending || revoke.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!link ? (
        // ---------- No link yet ----------
        <div className="text-center py-4">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-[#E5EAF0] shadow-sm">
            <Link2 className="h-6 w-6 text-genie-primary" />
          </div>
          <p className="text-sm text-[#64748B] mb-5 max-w-sm mx-auto">
            Generate a private link you can share with your team. They can open a clean editor for this document —
            and every Genie that uses it — without logging in. You can add a PIN or revoke it anytime.
          </p>
          <button
            onClick={() => generate.mutate(false)}
            disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl font-semibold shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 hover:scale-[1.02] disabled:opacity-50"
            style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
          >
            {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Create edit link
          </button>
        </div>
      ) : (
        // ---------- Link exists ----------
        <div className="space-y-5">
          {/* Link + copy */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">Shareable link</label>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={fullUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl bg-[#F9FAFC] text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-genie-primary"
              />
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-genie-primary bg-genie-primary/10 hover:bg-genie-primary/15 transition-colors shrink-0"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {/* PIN protection */}
          <div className="rounded-xl border border-[#E5EAF0] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {link.requiresPin ? (
                  <ShieldCheck className="h-5 w-5 text-genie-primary" />
                ) : (
                  <Lock className="h-5 w-5 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {link.requiresPin ? "PIN protected" : "No PIN"}
                  </p>
                  <p className="text-xs text-[#64748B]">
                    {link.requiresPin
                      ? "A PIN is required before anyone can save."
                      : "Anyone with the link can save. Add a PIN for extra safety."}
                  </p>
                </div>
              </div>
              {link.requiresPin && (
                <button
                  onClick={() => setPin.mutate(null)}
                  disabled={busy}
                  className="text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>

            {showPinForm ? (
              <div className="mt-4 flex items-center gap-2">
                <input
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="4–8 digits"
                  className="w-40 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base tracking-widest font-mono"
                />
                <button
                  onClick={handleSavePin}
                  disabled={pinValue.trim().length < 4 || setPin.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                >
                  {setPin.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save PIN
                </button>
                <button
                  onClick={() => {
                    setShowPinForm(false);
                    setPinValue("");
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowPinForm(true)}
                className="mt-3 text-sm font-medium text-genie-primary hover:underline"
              >
                {link.requiresPin ? "Change PIN" : "Add a PIN"}
              </button>
            )}
          </div>

          {/* Danger zone */}
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <button
              onClick={() => generate.mutate(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Regenerate link
            </button>
            <button
              onClick={() => revoke.mutate()}
              disabled={busy}
              className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              {revoke.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Revoke link
            </button>
          </div>
          <p className="text-xs text-[#64748B]">
            Regenerating replaces the link — the old URL stops working. Revoking removes it entirely.
          </p>
        </div>
      )}
    </div>
  );
};
